import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Spinner } from '../components/ui';

// OnboardingChoosePage is the fallback for a WorkOS-hosted "sign up" that
// arrives with no user_type AND an ambiguous origin (so the callback couldn't
// infer employer vs candidate). It carries a Purpose=onboarding token in the
// fragment — never a session — so nothing is bypassed: the user simply tells us
// which account to finish setting up, and we hand the token to that flow.
export default function OnboardingChoosePage(): JSX.Element {
  const navigate = useNavigate();
  // Capture the token once, then scrub the fragment so the bearer capability
  // never lingers in the address bar / history / referrer.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');

  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/signup', { replace: true });
    }
  }, [token, navigate]);

  function choose(kind: 'candidate' | 'employer'): void {
    // Hand the onboarding token to the chosen journey via the URL fragment.
    navigate(`/onboarding/${kind}#token=${encodeURIComponent(token)}`);
  }

  if (!token) {
    return (
      <div className="mx-auto flex w-full max-w-md px-4 py-8 sm:px-6 sm:py-10 xl:py-12">
        <Card variant="elevated" className="w-full text-center">
          <div className="flex flex-col items-center gap-3 py-4">
            <Spinner label="Preparing account choice" />
            <p className="text-sm text-text-secondary">Preparing your account choices…</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:py-12">
      <Card>
        <CardHeader title={<h1 className="whitespace-normal">How will you be using Reed.ai?</h1>} />
        <CardBody>
          <p className="mb-6 text-sm text-text-secondary">
            Tell us which account you&rsquo;d like to set up so we can finish your sign-up.
          </p>
          <div className="flex flex-col gap-3">
            <Button className="w-full" onClick={() => choose('candidate')}>
              I&rsquo;m looking for a job
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => choose('employer')}>
              I&rsquo;m hiring
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
